// Signing order with shared numbers (Documents tab PR 8d; mockup v3
// screen 7, "Signers & order").
//
// CONSUMER: shared/db/esign-send.ts (the number each signer is stored
// and sent to the provider with), shared/esign/send-signers.ts (reading
// a request's numbers), and the Signers & order screen (the same rules,
// so the screen and the send agree).
//
// THE RULE: with a signing order set, people who share a number sign
// together; number 2 is asked once everyone at number 1 has signed. The
// provider (BoldSign) treats a shared SignerOrder the same way, so the
// OS's own pacing and the provider's enforcement agree. Without a
// signing order nothing here changes anything: everyone is asked at
// once, exactly as before.
//
// Pure: no database, no request.

/** The most a request may number up to. More people than this in one
 *  queue is a different conversation, and a cap keeps a typo of 1000
 *  from reading as a thousand-step queue. */
export const MAX_SIGNING_ORDER = 50;

/** A request's order number for one signer, or null when it gave none or
 *  gave something that isn't a whole number from 1 to the cap. */
export function readSigningOrder(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isInteger(raw)) return null;
  return raw >= 1 && raw <= MAX_SIGNING_ORDER ? raw : null;
}

/**
 * The number each signer is stored and sent with.
 *
 * - No signing order: each person's position (1, 2, 3…), which is what
 *   every send has always stored. Nobody waits on anyone.
 * - A signing order, and every person who signs carries a number: those
 *   numbers squeezed to 1, 2, 3… with ties kept (5, 5, 9 → 1, 1, 2), so
 *   "who's next" never has to skip a gap. A copy recipient signs nothing
 *   and goes after everyone.
 * - A signing order with any number missing: positions, one person at a
 *   time, which is what "in this order" meant before shared numbers
 *   existed. A half-numbered list is never guessed at.
 */
export function signingRanks(
  signers: readonly { role: string; order?: number | null }[],
  enforceOrder: boolean,
): number[] {
  const positional = signers.map((_, i) => i + 1);
  if (!enforceOrder) return positional;
  const signing = signers.filter((s) => s.role === "signer");
  if (signing.length === 0 || signing.some((s) => readSigningOrder(s.order) === null)) {
    return positional;
  }
  const distinct = [...new Set(signing.map((s) => s.order as number))].sort((a, b) => a - b);
  const rankOf = new Map(distinct.map((n, i) => [n, i + 1]));
  const last = distinct.length + 1;
  return signers.map((s) => (s.role === "signer" ? (rankOf.get(s.order as number) ?? last) : last));
}

/** "Robert & Linda together, then Mark" for the review window; null when
 *  there is no order or only one step. */
export function describeSigningOrder(
  people: readonly { name: string; order: number }[],
): string | null {
  const groups = new Map<number, string[]>();
  for (const p of people) groups.set(p.order, [...(groups.get(p.order) ?? []), p.name]);
  const steps = [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, names]) => names);
  if (steps.length < 2 && !steps.some((s) => s.length > 1)) return null;
  const say = (names: string[]) =>
    names.length === 1
      ? names[0]!
      : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]} together`;
  return steps.map(say).join(", then ");
}
